import React, { useCallback, useEffect, useState, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Import slides
import { Slide1_Category } from '../components/slides/Slide1_Category';
import { Slide2_Problem } from '../components/slides/Slide2_Problem';
import { Slide3_System } from '../components/slides/Slide3_System';
import { Slide4_SystemVisual } from '../components/slides/Slide4_SystemVisual';
import { Slide5_Forge } from '../components/slides/Slide5_Forge';
import { Slide5_Proof } from '../components/slides/Slide5_Proof';
import { Slide7_Tiers } from '../components/slides/Slide7_Tiers';
import { Slide8_Investment } from '../components/slides/Slide8_Investment';
const SLIDES = [Slide1_Category, Slide2_Problem, Slide3_System, Slide4_SystemVisual, Slide5_Forge, Slide5_Proof, Slide8_Investment, Slide7_Tiers];
const SLIDE_LABELS = ['Capacity', 'Problem', 'Process', 'System', 'Sales School', 'Proof', 'Partnership', 'Tiers'];
const SYSTEM_SLIDE_INDEX = 3; // Index of Slide4_SystemVisual
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [figmaLoaded, setFigmaLoaded] = useState(false);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  const isSystemSlide = currentSlide === SYSTEM_SLIDE_INDEX;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 overflow-x-hidden relative selection:bg-[#7FE0C3]/30" data-id="element-776">
      {/* Pre-loaded Figma iframe - always mounted, only visible on System slide */}
      <div className={`fixed inset-0 z-10 transition-opacity duration-300 ${isSystemSlide ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} style={{
      paddingBottom: '8rem'
    }} data-id="element-777">
        <iframe src="https://embed.figma.com/board/VXiMkf6fYlJy2cjipBFyEw/The-HIRE-Advantage?node-id=0-1&embed-host=share" className="w-full h-full border-0" allowFullScreen onLoad={() => setFigmaLoaded(true)} data-id="element-778" />
      </div>

      {/* Main Content Area */}
      <div className="min-h-screen w-full relative flex flex-col" data-id="element-779">
        <AnimatePresence mode="wait" data-id="element-780">
          {!isSystemSlide && <CurrentSlideComponent key={currentSlide} data-id="element-781" />}
        </AnimatePresence>

        {/* System slide wrapper - shows when on system slide */}
        {isSystemSlide && <div className="min-h-screen w-full flex items-center justify-center px-4 sm:px-8 md:px-16 lg:px-24 py-20" data-id="element-782">
            <div className="w-full max-w-7xl" data-id="element-783">
              <div className="text-center space-y-3 mb-6" data-id="element-784">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white" data-id="element-785">
                  The HIRE Advantage System
                </h2>
                <p className="text-sm md:text-base text-slate-400" data-id="element-786">
                  Complete system architecture and workflow
                </p>
              </div>
              {!figmaLoaded && <div className="flex items-center justify-center py-20" data-id="element-787">
                  <div className="flex flex-col items-center space-y-3" data-id="element-788">
                    <div className="w-8 h-8 border-2 border-[#7FE0C3] border-t-transparent rounded-full animate-spin" data-id="element-789" />
                    <p className="text-slate-400 text-sm" data-id="element-790">
                      Loading system diagram...
                    </p>
                  </div>
                </div>}
            </div>
          </div>}

        {/* Spacer for bottom nav */}
        <div className="h-32 shrink-0" data-id="element-791" />
      </div>

      {/* Navigation Controls (Bottom) - Mobile Optimized */}
      <div className="fixed bottom-0 left-0 w-full p-3 sm:p-6 flex flex-col space-y-3 z-50 pointer-events-none bg-gradient-to-t from-[#0A0F1C] via-[#0A0F1C]/90 to-transparent" data-id="element-792">
        {/* Slide Labels Navigation */}
        <div className="flex items-center justify-center pointer-events-auto" data-id="element-793">
          <div className="flex items-center space-x-1 sm:space-x-2 bg-slate-900/90 backdrop-blur-sm border border-slate-800 rounded-full px-3 sm:px-4 py-2 shadow-xl overflow-x-auto max-w-full scrollbar-hide" data-id="element-794">
            {SLIDE_LABELS.map((label, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${idx === currentSlide ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} aria-label={`Go to ${label}`} data-id="element-795">
                {label}
              </button>)}
          </div>
        </div>

        {/* Logo and Arrow Controls */}
        <div className="flex items-center justify-between pointer-events-auto" data-id="element-796">
          <a href="https://thehireadvantage.co" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity" data-id="element-797">
            <img src="https://cdn.magicpatterns.com/uploads/f8eRPqMKBzam6b6SBPvfZ5/Full_white_logo.png" alt="HIRE" className="h-6 sm:h-8 hover:opacity-90 transition-opacity" data-id="element-798" />
          </a>

          <div className="flex items-center space-x-2" data-id="element-799">
            <button onClick={prevSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Previous slide" data-id="element-800">
              <ChevronLeft size={20} data-id="element-801" />
            </button>

            <button onClick={nextSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Next slide" data-id="element-802">
              <ChevronRight size={20} data-id="element-803" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}